import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Returns true if the given user currently has an active All-Access subscription.
 * Used to gate the "for me" option in the purchase choice dialog (so subscribers
 * can't double-buy a subscription, but can still gift one).
 */
export function useHasAllAccess(userId: string | undefined) {
  return useQuery({
    queryKey: ["has-all-access", userId],
    queryFn: async () => {
      if (!userId) return false;
      // Paused accounts get no access.
      const { data: profile } = await supabase
        .from("profiles")
        .select("is_paused")
        .eq("id", userId)
        .maybeSingle();
      if ((profile as any)?.is_paused) return false;

      // 1. Personal All-Access subscription
      const { data } = await supabase
        .from("user_subscriptions")
        .select("status, current_period_end")
        .eq("user_id", userId)
        .eq("subscription_kind", "all_access")
        .eq("status", "active")
        .maybeSingle();
      if (data) {
        const end = data.current_period_end ? new Date(data.current_period_end).getTime() : Infinity;
        if (end > Date.now()) return true;
      }

      // 2. Institution-granted access
      const { data: inst } = await supabase.rpc("has_active_institution_access", { _user_id: userId });
      return Boolean(inst);
    },
    enabled: !!userId,
    staleTime: 60_000,
  });
}
